import dayjs, { ManipulateType } from 'dayjs';
import { EventItem } from 'queries/events/types';
import { TIME_FORMAT_WITH_UTC } from 'config/constants';
import { Dayjs } from 'lib';
import { useMemo } from 'react';

export const filterUniqueItems = (items: string[], selectedOption: string): string[] => {
  const updatedItens = new Set(items);
  if (updatedItens.has(selectedOption)) {
    updatedItens.delete(selectedOption);
  } else {
    updatedItens.add(selectedOption);
  }
  return [...updatedItens];
};

export const getEndDate = (endDate?: string) => {
  return endDate === 'NaT' || !endDate ? dayjs().format(TIME_FORMAT_WITH_UTC) : endDate;
};

export const changePeriod = (
  alarmDateRange: { start: Dayjs | string; end: Dayjs | string },
  period: string,
) => {
  const alarmMapPeriod: { [key: number]: { unit: ManipulateType; value: number } } = {
    NaN: { unit: 'hour', value: 1 },
    6: { unit: 'hour', value: 3 },
    24: { unit: 'hour', value: 12 },
    168: { unit: 'day', value: 3.5 },
    720: { unit: 'day', value: 15 },
    1440: { unit: 'day', value: 30 },
    2160: { unit: 'day', value: 45 },
    4320: { unit: 'day', value: 90 },
  };

  const today = dayjs();
  const alarm_dt_start = dayjs(alarmDateRange.start).utc();
  let alarm_dt_end = dayjs(alarmDateRange.end).utc();

  let start = 1;
  let end = 1;
  const hours = parseInt(period, 10);

  const { value, unit } = alarmMapPeriod[hours];

  if (period !== 'alarm') {
    const end_diff = today.diff(alarm_dt_end, unit);

    if (value >= end_diff) {
      start = value;
      end = 0;
      alarm_dt_end = today;
    } else {
      start = value;
      end = value;
    }
  }

  if (value === 3.5) {
    return {
      start: alarm_dt_start.subtract(start, unit).subtract(12, 'hour'),
      end: alarm_dt_end.add(end, unit).add(12, 'hour'),
    };
  }

  return {
    start: alarm_dt_start.subtract(start, unit),
    end: alarm_dt_end.add(end, unit),
  };
};
export const getAlarmDateRange = (
  alarms: {
    start: string;
    end: string;
  }[],
  event: EventItem,
  format?: string,
) => {
  if (alarms.length > 0) {
    return {
      start: dayjs(alarms[alarms.length - 1].start, format),
      end: dayjs(getEndDate(alarms[0].end), format),
    };
  }
  return {
    start: dayjs(event.alarm_datetime_start, format),
    end: dayjs(getEndDate(event.alarm_datetime_end), format),
  };
};
export const getAlarmsInfoFromEvent = (event: EventItem) => {
  if (event.children && event.children.length > 0) {
    const childrenForAlarms = event.children;
    const alarms = childrenForAlarms.map((child) => ({
      start: child.alarm_datetime_start,
      end: child.alarm_datetime_end,
    }));
    return {
      alarms,
      alarmDateRange: getAlarmDateRange(alarms, event),
      closedAtAlarms: childrenForAlarms
        .filter((child) => !child.closed_at)
        .map((child) => child.closed_at as string),
    };
  }
  return {
    alarms: [{ start: event.alarm_datetime_start, end: getEndDate(event.alarm_datetime_end) }],
    alarmDateRange: getAlarmDateRange([], event),
    closedAtAlarms: [event.closed_at as string],
  };
};
export const parseAssetTags = (data?: {
  categories: {
    [key: string]: Array<{
      reference: string;
      category?: string;
      unit_of_measurement?: string;
      name: string;
    }>;
  };
}) => {
  let parsedAssetTags: ChartTag[] = [];
  if (data?.categories) {
    Object.keys(data.categories).forEach((catKey) => {
      const category = data.categories[catKey];

      if (Array.isArray(category)) {
        parsedAssetTags = [
          ...parsedAssetTags,
          ...category.reduce((acc, tag) => {
            if (!tag.reference) {
              return acc;
            }
            if (acc.find((item) => item.reference === tag.reference)) {
              return [
                ...acc.filter((item) => item.reference !== tag.reference),
                { ...tag, unit_of_measurement: tag.unit_of_measurement ?? '', category: [catKey] },
              ];
            }
            return [
              ...acc,
              { ...tag, unit_of_measurement: tag.unit_of_measurement ?? '', category: [catKey] },
            ];
          }, [] as ChartTag[]),
        ];
      }
    });
  }

  return parsedAssetTags;
};

export const useDatesHelper = () => {
  const today = useMemo(() => dayjs(), []);
  return {
    buildUTCDateRange: (period: string | number) => ({
      start: today.subtract(+period, 'hour'),
      end: today,
    }),
  };
};
